import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadFullDictionary } from '../../shared/dictionary';
import type { GauntletAware } from '../../shared/gauntletStep';
import {
  bestUnrevealedLetter,
  isMistake,
  isSolved as computeSolved,
  letterFrequencies,
  REJECTION_MESSAGE,
  validate,
  type CipherPuzzle,
  type RejectionReason,
} from './types';
import './cipher.css';

interface Guess {
  word: string;
  accepted: boolean;
  reason?: RejectionReason;
  newlyRevealed: string[];
}

/**
 * Cipher play surface. Gauntlet-only today — the host supplies the puzzle and
 * owns lives, hints and the timer, so this component reports outcomes upward
 * rather than rendering its own completion overlay.
 */
export default function CipherPlay({
  puzzle,
  gauntlet,
  onSolved,
}: GauntletAware & {
  puzzle: CipherPuzzle;
  /** Standalone completion callback, used when `gauntlet` is absent. */
  onSolved?: () => void;
}) {
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [history, setHistory] = useState<Guess[]>([]);
  const [input, setInput] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [dict, setDict] = useState<Set<string> | null>(null);

  const solvedRef = useRef(false);
  const shakeTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    loadFullDictionary().then(setDict);
    return () => clearTimeout(shakeTimer.current);
  }, []);

  const solved = useMemo(() => computeSolved(puzzle, revealed), [puzzle, revealed]);

  // Fire the solved callback exactly once, even though `solved` stays true.
  useEffect(() => {
    if (solved && !solvedRef.current) {
      solvedRef.current = true;
      gauntlet?.onSolved();
      onSolved?.();
    }
  }, [solved, gauntlet, onSolved]);

  /** Reveal the highest-frequency unrevealed letter — one per host hint. */
  const applyHint = useCallback(() => {
    setRevealed((prev) => {
      if (computeSolved(puzzle, prev)) return prev;
      const letter = bestUnrevealedLetter(puzzle, prev);
      if (!letter) return prev;
      const plain = puzzle.inverseSubstitution[letter];
      if (!plain) return prev;
      return { ...prev, [letter]: plain };
    });
  }, [puzzle]);

  // The host owns the hint pool; each increment of hintSignal grants one.
  const lastHintSignal = useRef(gauntlet?.hintSignal ?? 0);
  useEffect(() => {
    const signal = gauntlet?.hintSignal ?? 0;
    if (signal > lastHintSignal.current) {
      lastHintSignal.current = signal;
      applyHint();
    }
  }, [gauntlet?.hintSignal, applyHint]);

  const triggerShake = useCallback(() => {
    setShake(true);
    clearTimeout(shakeTimer.current);
    shakeTimer.current = window.setTimeout(() => setShake(false), 420);
  }, []);

  const submit = useCallback(() => {
    if (solved) return;
    const raw = input.trim();
    if (!raw) return;

    const outcome = validate(
      raw,
      puzzle,
      revealed,
      (w) => dict?.has(w.toUpperCase()) ?? false,
    );
    const word = raw.toUpperCase().replace(/[^A-Z]/g, '');

    if (outcome.kind === 'accepted') {
      setRevealed((prev) => ({ ...prev, ...outcome.newRevealed }));
      setHistory((prev) => [
        { word, accepted: true, newlyRevealed: Object.keys(outcome.newRevealed) },
        ...prev,
      ]);
      setFeedback(null);
      setInput('');
      return;
    }

    setHistory((prev) => [
      { word, accepted: false, reason: outcome.reason, newlyRevealed: [] },
      ...prev,
    ]);
    setFeedback(REJECTION_MESSAGE[outcome.reason]);
    triggerShake();
    setInput('');
    // Typos are free; only a genuine wrong guess costs a life.
    if (isMistake(outcome.reason)) gauntlet?.onWrong();
  }, [solved, input, puzzle, revealed, dict, triggerShake, gauntlet]);

  const frequencies = useMemo(() => letterFrequencies(puzzle), [puzzle]);
  const maxFreq = frequencies[0]?.count ?? 1;

  // Split into words so the display wraps on word boundaries, never mid-word.
  const cipherWords = useMemo(() => puzzle.cipherText.split(/\s+/), [puzzle.cipherText]);

  return (
    <div className="cp-board">
      <div className={'cp-display' + (shake ? ' is-shake' : '')}>
        {cipherWords.map((word, wi) => (
          <span key={wi} className="cp-word">
            {[...word].map((ch, ci) => {
              const isLetter = /[A-Za-z]/.test(ch);
              if (!isLetter) {
                return (
                  <span key={ci} className="cp-punct">
                    {ch}
                  </span>
                );
              }
              const up = ch.toUpperCase();
              const plain = revealed[up];
              return (
                <span
                  key={ci}
                  className={'cp-cell' + (plain ? ' is-revealed' : '')}
                >
                  <span className="cp-plain">{plain ?? ''}</span>
                  <span className="cp-cipher">{up}</span>
                </span>
              );
            })}
          </span>
        ))}
      </div>

      {solved && (
        <div className="cp-solution">
          <div className="cp-solution-text">{puzzle.sourceSentence}</div>
          {puzzle.sourceAttribution && (
            <div className="cp-solution-attr">— {puzzle.sourceAttribution}</div>
          )}
        </div>
      )}

      {!solved && (
        <>
          <form
            className="cp-input-row"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <input
              className="cp-input"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                if (feedback) setFeedback(null);
              }}
              placeholder="Guess a word in the quote…"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              aria-label="Guess a word"
            />
            <button type="submit" className="btn btn-primary" disabled={!input.trim()}>
              Commit
            </button>
          </form>

          {feedback && <div className="cp-feedback">{feedback}</div>}

          <div className="cp-freq">
            <div className="cp-freq-title">CIPHER LETTER FREQUENCY</div>
            {frequencies.slice(0, 6).map(({ letter, count }) => (
              <div key={letter} className="cp-freq-row">
                <span className={'cp-freq-letter' + (revealed[letter] ? ' is-known' : '')}>
                  {revealed[letter] ?? letter}
                </span>
                <span className="cp-freq-bar">
                  <span
                    className="cp-freq-fill"
                    style={{ width: `${(count / maxFreq) * 100}%` }}
                  />
                </span>
                <span className="cp-freq-count">{count}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {history.length > 0 && (
        <div className="cp-history">
          {history.slice(0, 6).map((g, i) => (
            <div
              key={`${g.word}-${i}`}
              className={'cp-history-row' + (g.accepted ? ' is-ok' : ' is-bad')}
            >
              <span className="cp-history-word">{g.word}</span>
              <span className="cp-history-note">
                {g.accepted
                  ? `+${g.newlyRevealed.length} letter${
                      g.newlyRevealed.length === 1 ? '' : 's'
                    }`
                  : g.reason && REJECTION_MESSAGE[g.reason]}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
